import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { CircleNotch, Rocket, ArrowClockwise, Browser } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const T = '#00A0D2';

const BUSINESS_TYPES = ['Restaurant', 'Cafe / Bakery', 'Law Firm', 'Dentist / Clinic', 'Real Estate', 'Gym / Fitness', 'Salon / Spa', 'Photography', 'Consulting Agency', 'E-commerce Store', 'Portfolio', 'Blog', 'Non-profit', 'Construction', 'Automotive', 'Education / Coaching', 'Travel / Tourism', 'Other'];

const WPBNew = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ account_id: '', domain: '', business_type: 'Restaurant', description: '', site_title: '' });

  useEffect(() => {
    axios.get(`${API}/api/wp-builder/accounts`, { withCredentials: true })
      .then(r => { setAccounts(r.data); if (r.data[0]) setForm(f => ({ ...f, account_id: r.data[0].id })); })
      .catch(() => {});
  }, []);

  const loadDomains = async (accId) => {
    setLoadingDomains(true); setDomains([]);
    try {
      const r = await axios.get(`${API}/api/wp-builder/domains`, { params: accId ? { account_id: accId } : {}, withCredentials: true });
      setDomains(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load domains'); } finally { setLoadingDomains(false); }
  };
  useEffect(() => { if (form.account_id) loadDomains(form.account_id); }, [form.account_id]);

  const submit = async () => {
    if (!form.account_id) { toast.error('Add & select a builder account first'); return; }
    if (!form.domain) { toast.error('Pick or type a domain'); return; }
    if (form.description.trim().length < 10) { toast.error('Describe the business a bit more (min 10 chars)'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/wp-builder/sites`, form, { withCredentials: true });
      toast.success('Build started — pointing DNS to Cloudflare');
      navigate('/wordpress/sites');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8 max-w-3xl animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div><h1 className="text-4xl font-black mb-2 flex items-center gap-3" style={{ color: T }}><Browser size={38} weight="fill" /> NEW WORDPRESS SITE</h1>
        <p className="text-slate-400 font-mono">Domain → Cloudflare DNS → cPanel WordPress → AI-built content.</p></div>

      {accounts.length === 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 p-4 text-sm">
          No builder account yet. <button onClick={() => navigate('/wordpress/accounts')} className="underline font-bold">Add one</button> (Cloudflare token + server IP) to begin.
        </div>
      )}

      <div className="bg-card border border-white/10 p-6 space-y-5">
        <div>
          <label className="text-xs text-slate-500 uppercase tracking-wider">Builder account</label>
          <select value={form.account_id} onChange={e => setForm({ ...form, account_id: e.target.value })} data-testid="wpb-new-account" className="w-full bg-[#111318] border border-white/10 p-2.5 text-white text-sm mt-1">
            <option value="">Select…</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.label} · {a.server_ip}</option>)}
          </select>
        </div>

        <div>
          <label className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-2">Domain (from Namecheap)
            {form.account_id && <button onClick={() => loadDomains(form.account_id)} className="text-slate-400 hover:text-white"><ArrowClockwise size={13} className={loadingDomains ? 'animate-spin' : ''} /></button>}
          </label>
          <select value={domains.some(d => d.name === form.domain) ? form.domain : ''} onChange={e => setForm({ ...form, domain: e.target.value })} data-testid="wpb-new-domain" className="w-full bg-[#111318] border border-white/10 p-2.5 text-white text-sm mt-1">
            <option value="">{loadingDomains ? 'Loading domains…' : (domains.length ? 'Select a domain…' : 'No domains found — type below')}</option>
            {domains.map(d => <option key={d.name} value={d.name}>{d.name}{d.expires ? ` (exp ${d.expires})` : ''}</option>)}
          </select>
          <input value={form.domain} onChange={e => setForm({ ...form, domain: e.target.value })} placeholder="or type a domain e.g. mybrand.com" data-testid="wpb-new-domain-text" className="w-full bg-[#111318] border border-white/10 p-2.5 text-white text-sm mt-2 focus:outline-none focus:border-[#00A0D2]" />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider">Business type</label>
            <select value={form.business_type} onChange={e => setForm({ ...form, business_type: e.target.value })} data-testid="wpb-new-type" className="w-full bg-[#111318] border border-white/10 p-2.5 text-white text-sm mt-1">
              {BUSINESS_TYPES.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider">Site title (optional)</label>
            <input value={form.site_title} onChange={e => setForm({ ...form, site_title: e.target.value })} placeholder="e.g. Bella's Bistro" data-testid="wpb-new-title" className="w-full bg-[#111318] border border-white/10 p-2.5 text-white text-sm mt-1 focus:outline-none focus:border-[#00A0D2]" />
          </div>
        </div>

        <div>
          <label className="text-xs text-slate-500 uppercase tracking-wider">Describe the business (the AI uses this to write the site)</label>
          <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={4} placeholder="e.g. A cozy Italian bistro in downtown Austin serving handmade pasta, wood-fired pizza and local wines. Family-owned, open for lunch and dinner." data-testid="wpb-new-desc" className="w-full bg-[#111318] border border-white/10 p-2.5 text-white text-sm mt-1 focus:outline-none focus:border-[#00A0D2]" />
        </div>

        <button onClick={submit} disabled={busy} data-testid="wpb-build-btn" className="flex items-center gap-2 text-white px-6 py-3 font-bold text-sm uppercase tracking-wider disabled:opacity-50" style={{ background: T }}>
          {busy ? <CircleNotch size={18} className="animate-spin" /> : <Rocket size={18} />} Start Build
        </button>
        <p className="text-[11px] text-slate-500">This creates a Cloudflare zone, points your Namecheap nameservers to Cloudflare, and sets proxied A records to your server IP. Then you add the domain in cPanel to continue.</p>
      </div>
    </div>
  );
};
export default WPBNew;
